import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Typography, Box, Stack, useMediaQuery } from "@mui/material";
import {
  ASTableV2,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2Cell,
  ASTableV2Body,
  mobileWidth
} from "@qubit/autoparts";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { ShiftSelectSnackbar } from "~/components/ShiftSelectSnackbar";
import { NoOrders } from "~/features/orders/NoOrders";
import { OrdersSearch, OrderStatus } from "~/features/orders/OrdersSearch";
import { usePagination } from "~/hooks/usePagination";
import { useShiftSelect } from "~/hooks/useShiftSelect";
import { formatTime } from "~/lib/helpers";
import { OrderSummaryDto } from "~/types/api";

import { OrderTableWrapperProps as OrderTableV2Props } from "./OrderTableWrapper";

export function OrderTableV2(props: OrderTableV2Props) {
  const {
    formatOrderStatus = undefined,
    fulfillmentCenter,
    onOrderSelected,
    orders,
    ordersLoading,
    selectedOrderIds,
    ordersError,
    selectedDate,
    handleDateChange
  } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(mobileWidth);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    currentPageRows: ordersToDisplay
  } = usePagination<OrderSummaryDto>({
    rows: orders,
    rowsPerPage: 8,
    disablePaginationOnMobile: isMobile
  });

  const { shiftIsPressed, handleRowClick, shiftClickFunction } =
    useShiftSelect<OrderSummaryDto>({
      rows: orders,
      selectedRowIds: selectedOrderIds,
      getRowId: (order) => order.orderId,
      onRowSelected: onOrderSelected
    });

  const defaultOrderStatusFormat = (order: OrderSummaryDto) => order.status;
  const orderStatusFormatter = formatOrderStatus || defaultOrderStatusFormat;
  const orderStatus = useCallback(
    (order: OrderSummaryDto) =>
      !formatOrderStatus
        ? t(`${orderStatusFormatter(order).toLowerCase()}` as OrderStatus)
        : orderStatusFormatter(order),
    [formatOrderStatus, orderStatusFormatter, t]
  );

  const noOrders =
    !ordersLoading && orders && orders.length === 0 && !ordersError;

  return (
    <>
      <Box
        id="order-table-container"
        sx={{ height: "100%", overflow: isMobile ? "auto" : "hidden" }}
      >
        <ASTableV2
          gridTemplateColumns={isMobile ? "2fr 1fr 1fr" : "3fr 1fr 1fr"}
          gridTemplateRows={
            isMobile
              ? `50px ${
                  noOrders
                    ? "auto"
                    : `repeat(${Math.min(ordersToDisplay.length, 8)}, 120px)`
                }`
              : `auto ${
                  noOrders
                    ? "auto"
                    : `repeat(${ordersToDisplay.length}, minmax(80px, 1fr))`
                }`
          }
          isLoading={ordersLoading}
          pagination={
            isMobile
              ? undefined
              : {
                  page: currentPage,
                  pageCount: totalPages,
                  setPage: (p) => setCurrentPage(p)
                }
          }
          searchComponent={<OrdersSearch />}
          datePicker={{ selectedDate, onDateChange: handleDateChange }}
          containerSx={isMobile ? { height: "100%", overflow: "auto" } : {}}
        >
          <ASTableV2Header>
            <ASTableV2Row>
              <ASTableV2Cell>
                <Typography fontSize={isMobile ? "1rem" : "1.25rem"}>
                  {props.title}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography fontSize={isMobile ? "1rem" : "1.25rem"}>
                  {isMobile ? t("time") : t("window")}
                </Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography fontSize={isMobile ? "1rem" : "1.25rem"}>
                  {t("status")}
                </Typography>
              </ASTableV2Cell>
            </ASTableV2Row>
          </ASTableV2Header>

          <ASTableV2Body>
            {noOrders && (
              <ASTableV2Row>
                <ASTableV2Cell
                  sx={{
                    backgroundColor: "transparent",
                    gridColumn: "1 / -1"
                  }}
                >
                  <NoOrders
                    customSx={{
                      width: "100%"
                    }}
                  />
                </ASTableV2Cell>
              </ASTableV2Row>
            )}
            {!noOrders &&
              ordersToDisplay.map((order) => (
                <ASTableV2Row
                  key={order.orderId}
                  selected={props.selectedOrderIds.includes(order.orderId)}
                  onClick={() => handleRowClick(order)}
                  role="button"
                  tabIndex={0}
                >
                  <ASTableV2Cell
                    sx={{
                      wordBreak: "break-word"
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="flex-start"
                      gap={isMobile ? 1 : 1.5}
                    >
                      <Box>
                        <Typography variant={isMobile ? "body2" : "subtitle1"}>
                          {order.customerName}
                        </Typography>
                        <Typography
                          variant={isMobile ? "body2" : "subtitle1"}
                          component="div"
                        >
                          {order.externalOrderId}
                        </Typography>
                      </Box>

                      {order.priority.toLowerCase() === "express" && (
                        <Stack direction="column" alignItems="center" gap={0.5}>
                          <AccessTimeIcon fontSize="medium" />
                          <Typography variant="caption">
                            {order.priority}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                  </ASTableV2Cell>
                  <ASTableV2Cell>
                    <Typography variant={isMobile ? "body2" : "subtitle1"}>
                      {order.pickingEndTime &&
                        formatTime(
                          order.pickingEndTime,
                          fulfillmentCenter?.timeZone
                        )}
                    </Typography>
                  </ASTableV2Cell>
                  <ASTableV2Cell>
                    <Typography variant={isMobile ? "body2" : "subtitle1"}>
                      {orderStatus(order)}
                    </Typography>
                  </ASTableV2Cell>
                </ASTableV2Row>
              ))}
          </ASTableV2Body>
        </ASTableV2>
      </Box>
      <ShiftSelectSnackbar isOpen={shiftIsPressed} mode={shiftClickFunction} />
    </>
  );
}

export default OrderTableV2;
